import { Flex } from "@/components/atoms/flex";
import { useIntl } from "react-intl";
import { useStyle } from "./style";
import { Text, Radio, RadioGroup } from "@fluentui/react-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TransferToAgentProps } from "./types";
import { isValidRouteCallDestination } from "@/common/helpers/validationHelper";

import { WarmTransfer } from "@/common/constants";
import { TransferType, TransferChannelType } from "@/common/types/transfer";
import { useTransferHook } from "@/hooks/transfer";
import { AppSettingsState } from "@/common/stores/persistingStores/app/types";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { addBreadcrumb } from "@/common/helpers/sentryHelper";
import { AgentSearchDialog } from "@/components/organisms/AgentSearch";

export const InteractionTransferToAgentPopover = (
	props: TransferToAgentProps
) => {
	const intl = useIntl();
	const classes = useStyle();

	const settings = useAppSettingsStore((state: AppSettingsState) => state.settings);

	const [startTransfer] = useTransferHook(
		props.channelType ?? TransferChannelType.Voice
	);

	const [transferType, setTransferType] = useState(
		TransferType.AgentColdCallTransfer
	);

	const initiateTransfer = useCallback(
		(selectedAgent: string) => {
			addBreadcrumb("ui_interaction", "Initiating transfer to agent", {
				transferTarget: selectedAgent,
				transferType,
				channelType: props.channelType,
			});

			startTransfer(
				transferType,
				selectedAgent,
				props.messagingId,
				props.onTransferCallback
			);
		},
		[
			transferType,
			startTransfer,
			props.channelType,
			props.messagingId,
			props.onTransferCallback,
		]
	);

	useEffect(() => {
		if (props.channelType === TransferChannelType.Voice) {
			const coldEnabled = settings.callColdTransferEnabled;
			const warmEnabled = settings.callWarmTransferEnabled;
			const routeEnabled = settings?.routeCallEnabled;

			if (!coldEnabled && !warmEnabled) {
				if (routeEnabled) {
					setTransferType(TransferType.AgentRouteCallTransfer);
				} else {
					setTransferType(undefined as unknown as TransferType);
				}
			} else {
				setTransferType(
					settings.callPreferredTransferType === WarmTransfer && warmEnabled
						? TransferType.AgentWarmCallTransfer
						: TransferType.AgentColdCallTransfer
				);
			}
		}
	}, [settings, props.channelType]);

	const buildTransferOptionLabel = (
		enabled: boolean,
		actionNameTranslationKey: string
	) => {
		if (enabled) {
			return "";
		}

		return intl.formatMessage(
			{
				id: "Interaction.Buttons.ActionDisabledForTenant",
			},
			{
				action: intl.formatMessage({
					id: actionNameTranslationKey,
				}),
			}
		);
	};

	// Determine the appropriate placeholder and action button text based on transfer type
	const getPlaceholderText = () => {
		if (
			transferType.toString() === TransferType.AgentRouteCallTransfer.toString()
		) {
			return intl.formatMessage({ id: "Transfer.InputPlaceholder" });
		}
		return props.channelType === TransferChannelType.Voice
			? intl.formatMessage({ id: "Transfer.InputPlaceholder" })
			: intl.formatMessage({ id: "Transfer.InputPlaceholderMessaging" });
	};

	const getActionButtonText = () => {
		return intl.formatMessage({ id: "Transfer.ActionButton" });
	};

	// Custom validation for route call transfers
	const handleRouteCallAction = useCallback(
		(selectedAgent: string) => {
			if (
				transferType.toString() ===
				TransferType.AgentRouteCallTransfer.toString()
			) {
				// Apply route call specific validation
				if (!isValidRouteCallDestination(selectedAgent)) {
					// We could show an error, but the AgentSearchDialog handles validation internally
					// For now, we'll let the transfer proceed and let the backend handle validation
				}
			}
			initiateTransfer(selectedAgent);
		},
		[transferType, initiateTransfer]
	);

	const getTitleKey = () => {
		return "TransferToAgent";
	};

	const actionEnabled = useMemo(
		() =>
			transferType !== TransferType.AgentRouteCallTransfer ||
			!props.isInteractionMonitored,
		[transferType, props.isInteractionMonitored]
	);

	// Single UI path using AgentSearchDialog for all transfer types
	return (
		<Flex column className={classes.container}>
			<Text weight="semibold" size={200} className={classes.title}>
				{intl.formatMessage({
					id: `Interaction.Buttons.${getTitleKey()}`,
				})}
			</Text>
			{props.channelType === TransferChannelType.Voice && (
				<RadioGroup
					layout="horizontal"
					className={classes.transferType}
					value={transferType.toString()}
					onChange={(_, data) => {
						setTransferType(Number(data.value) as unknown as TransferType);
					}}
				>
					<Radio
						value={TransferType.AgentColdCallTransfer.toString()}
						label={intl.formatMessage({
							id: "Interaction.Buttons.ColdTransfer",
						})}
						title={buildTransferOptionLabel(
							settings?.callColdTransferEnabled,
							"Interaction.Buttons.ColdTransfer"
						)}
						disabled={!settings.callColdTransferEnabled}
					/>
					<Radio
						value={TransferType.AgentWarmCallTransfer.toString()}
						label={intl.formatMessage({
							id: "Interaction.Buttons.WarmTransfer",
						})}
						title={buildTransferOptionLabel(
							settings?.callWarmTransferEnabled,
							"Interaction.Buttons.WarmTransfer"
						)}
						disabled={!settings.callWarmTransferEnabled}
					/>
					<Radio
						value={TransferType.AgentRouteCallTransfer.toString()}
						label={intl.formatMessage({
							id: "Interaction.Buttons.RouteCall",
						})}
						title={buildTransferOptionLabel(
							settings?.routeCallEnabled,
							"Interaction.Buttons.RouteCall"
						)}
						disabled={!settings?.routeCallEnabled}
					/>
				</RadioGroup>
			)}
			<div className={classes.agentSearchContainer}>
				<AgentSearchDialog
					onAction={handleRouteCallAction}
					onCancel={props.closePopover}
					channelType={props.channelType}
					title=""
					placeholder={getPlaceholderText()}
					actionButtonText={getActionButtonText()}
					actionDisabled={!actionEnabled}
					actionDisabledTooltip={intl.formatMessage({ id: "Interaction.Buttons.RouteCallDisabled" })}
				/>
			</div>
		</Flex>
	);
};
